import { WriterThreadProvider } from '@/components/writer/WriterThreadProvider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, PanelRight, Pencil } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { useWriterEnabled } from '@/hooks/useWriterEnabled';
import { useWriterCompiledPdf } from '@/hooks/useWriterCompiledPdf';
import { useWriterVersions } from '@/hooks/useWriterVersions';
import { useWriterCompileNotifications } from '@/hooks/useWriterCompileNotifications';
import { supabase } from '@/integrations/supabase/client';
import WriterChatPane from '@/components/writer/WriterChatPane';
import WriterBibliographyPanel from '@/components/writer/WriterBibliographyPanel';
import WriterMemoryPanel from '@/components/writer/WriterMemoryPanel';
import WriterPdfPane from '@/components/writer/WriterPdfPane';
import WriterPdfSheet from '@/components/writer/WriterPdfSheet';
import WriterDraftSheet from '@/components/writer/WriterDraftSheet';
import WriterMobileStatusBar from '@/components/writer/WriterMobileStatusBar';
import WriterPaperMapPanel from '@/components/writer/WriterPaperMapPanel';
import { useWriterPaperMap } from '@/hooks/useWriterPaperMap';
import { useWriterLayoutMode } from '@/hooks/useWriterLayoutMode';
import SubmitAsVersionButton from '@/components/writer/SubmitAsVersionButton';
import { useWriterDocState } from '@/hooks/useWriterDocState';
import { countWriterDiagnostics } from '@/lib/writer/diagnosticSeverity';


import WriterVersionScrubber, {
  type WriterVersionSummary,
} from '@/components/writer/WriterVersionScrubber';
import { revertToVersion, forkFromVersion } from '@/lib/writer/versionActions';
import { enqueueWriterCompile } from '@/lib/writer/compileQueue';
import { buildPdfVersionSignature } from '@/lib/writer/pdfVersionSignature';
import { useCompileSettleWatch } from '@/hooks/useCompileSettleWatch';

import {
  renameWriterProject,
  MAX_TITLE_LENGTH,
} from '@/lib/writer/projectTitle';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useHorizontalSwipe } from '@/hooks/useSwipeNavigation';


type ProjectRow = {
  id: string;
  title: string;
  published_paper_id: string | null;
  updated_at: string;
};

const WriterProject: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [refreshTick, setRefreshTick] = useState(0);
  const { enabled, loading: flagLoading } = useWriterEnabled();
  const [project, setProject] = useState<ProjectRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { versions, reload: loadVersions } = useWriterVersions(projectId);
  useWriterCompileNotifications(versions);
  const {
    paperMap,
    diagnostics,
    loading: paperMapLoading,
  } = useWriterPaperMap(projectId, refreshTick);
  // See buildPdfVersionSignature: narrow, marked-aware refetch key.
  const versionSignature = buildPdfVersionSignature(versions);


  // Fires one forced PDF refetch once an explicitly re-queued compile
  // reaches a terminal state — the Realtime-independent safety net.
  const watchCompileSettled = useCompileSettleWatch(
    useCallback(() => {
      setRefreshTick((t) => t + 1);
      void loadVersions();
    }, [loadVersions]),
  );

  const pdf = useWriterCompiledPdf(projectId, refreshTick, versionSignature);
  const { docState } = useWriterDocState(projectId);
  const [busyVersionId, setBusyVersionId] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const layoutMode = useWriterLayoutMode();
  const isCompact = layoutMode !== 'desktop';
  const { problemCount, warningCount: diagnosticWarningCount } = countWriterDiagnostics(diagnostics);
  const warningCount = diagnosticWarningCount;
  const [pdfSheetOpen, setPdfSheetOpen] = useState(false);
  const [draftSheetOpen, setDraftSheetOpen] = useState(false);
  // Swipe-left on the chat surface opens the compiled draft. The segmented
  // control in the status bar stays the discoverable primary affordance.
  const swipe = useHorizontalSwipe({
    enabled: isCompact && !pdfSheetOpen,
    onSwipeLeft: () => setPdfSheetOpen(true),
  });


  useEffect(() => {
    if (!enabled || !projectId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('writer_projects')
        .select('id, title, published_paper_id, updated_at')
        .eq('id', projectId)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setNotFound(true);
      } else {
        setProject(data as ProjectRow);
      }
      setLoading(false);
      loadVersions();
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, projectId, loadVersions]);


  const handleRevert = useCallback(
    async (v: WriterVersionSummary) => {
      if (!projectId) return;
      setBusyVersionId(v.id);
      try {
        await revertToVersion({ projectId, versionId: v.id });
        toast.success('Reverted — new pending version created');
        setRefreshTick((t) => t + 1);
        await loadVersions();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to revert version',
        );
      } finally {
        setBusyVersionId(null);
      }
    },
    [projectId, loadVersions],
  );

  const handleFork = useCallback(
    async (v: WriterVersionSummary) => {
      if (!projectId) return;
      setBusyVersionId(v.id);
      try {
        const res = await forkFromVersion({ projectId, versionId: v.id });
        toast.success('Forked into a new draft');
        navigate(`/write/${res.project_id}`);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to fork version',
        );
      } finally {
        setBusyVersionId(null);
      }
    },
    [projectId, navigate],
  );

  const handleRetryCompile = useCallback(
    async (v: WriterVersionSummary) => {
      setBusyVersionId(v.id);
      try {
        await enqueueWriterCompile({ versionId: v.id });
        toast.success('Compile re-queued');
        await loadVersions();
        // Guarantee a refresh even if Realtime never reports the transition:
        // a same-version recompile keeps id/artifact_path, so without this the
        // viewer could keep serving the previous PDF.
        void watchCompileSettled(v.id);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to re-queue compile',
        );
      } finally {
        setBusyVersionId(null);
      }
    },
    [loadVersions, watchCompileSettled],
  );

  // Banner retry: reassemble from the current structured tables and
  // compile the fresh snapshot. Recovers from stale main_tex produced by
  // an older assembler (e.g. missing tikz auto-detection) — a plain
  // "compile again" would just replay the same broken bytes.
  const handleReassembleAndCompile = useCallback(
    async (v: WriterVersionSummary) => {
      if (!projectId) return;
      setBusyVersionId(v.id);
      try {
        const { error } = await supabase.functions.invoke('writer-materialize', {
          body: { project_id: projectId, force: true, label: 'Manual reassemble' },
        });
        if (error) throw error;
        toast.success('Reassembled — compiling latest');
        await loadVersions();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to reassemble',
        );
      } finally {
        setBusyVersionId(null);
      }
    },
    [projectId, loadVersions],
  );


  // The draft name mirrors the paper's \title{...}: a database trigger
  // updates writer_projects.title, and we listen for that change here.
  useEffect(() => {
    if (!enabled || !projectId) return;
    const channel = supabase
      .channel(`writer-project-title-${projectId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'writer_projects', filter: `id=eq.${projectId}` },
        (payload) => {
          const title = (payload.new as { title?: string } | null)?.title;
          if (title) setProject((p) => (p && p.title !== title ? { ...p, title } : p));
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [enabled, projectId]);

  const startRename = useCallback(() => {
    if (!project) return;
    setRenameDraft(project.title);
    setRenaming(true);
    requestAnimationFrame(() => renameInputRef.current?.select());
  }, [project]);

  const cancelRename = useCallback(() => {
    setRenaming(false);
    setRenameDraft('');
  }, []);

  const commitRename = useCallback(async () => {
    if (!projectId || !project) return;
    const trimmed = renameDraft.trim();
    if (!trimmed || trimmed === project.title) {
      cancelRename();
      return;
    }
    try {
      const saved = await renameWriterProject(projectId, trimmed);
      setProject((p) => (p ? { ...p, title: saved } : p));
      setRefreshTick((t) => t + 1);
      // A fresh draft has no editable sections yet, so there is nothing to
      // rebuild; the title is kept and shows up with Writer's first edit.
      const { count } = await supabase
        .from('writer_project_sections')
        .select('id', { count: 'exact', head: true })
        .eq('project_id', projectId);
      if ((count ?? 0) > 0) {
        toast.success('Title updated in your paper');
        void supabase.functions
          .invoke('writer-materialize', { body: { project_id: projectId } })
          .then(() => loadVersions());
      } else {
        toast.success('Title saved. It appears in the PDF with Writer\'s first edit.');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Rename failed');
    } finally {
      setRenaming(false);
      setRenameDraft('');
    }
  }, [projectId, project, renameDraft, cancelRename, loadVersions]);


  if (flagLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-24 text-center">
          <h1 className="text-2xl font-semibold mb-3">Writer is temporarily unavailable</h1>
          <p className="text-muted-foreground">PaperVerse Writer is switched off platform-wide. Please check back shortly.</p>
        </main>
        <Footer />
      </div>
    );
  }
  if (notFound) return <Navigate to="/write" replace />;

  const pdfPaneProps = {
    versions,
    busyVersionId,
    docState,
    pdf,
    onRefreshVersions: loadVersions,
    onRevert: handleRevert,
    onFork: handleFork,
    onRetryCompile: handleRetryCompile,
    onReassemble: handleReassembleAndCompile,
    onReload: () => setRefreshTick((t) => t + 1),
  };

  const headerActions =
    projectId && project ? (
      <>
        <SubmitAsVersionButton
          projectId={projectId}
          paperId={project.published_paper_id}
          projectTitle={project.title}
        />
        <WriterMemoryPanel projectId={projectId} />
      </>
    ) : null;

  const titleControl = renaming ? (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void commitRename();
      }}
      className="flex items-center gap-2 w-full max-w-md"
    >
      <input
        ref={renameInputRef}
        value={renameDraft}
        onChange={(e) => setRenameDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            cancelRename();
          }
        }}
        onBlur={() => void commitRename()}
        maxLength={MAX_TITLE_LENGTH}
        aria-label="Rename draft"
        className="w-full text-sm font-medium bg-transparent border-b border-primary/60 outline-none focus:border-primary px-1 py-0.5"
      />
    </form>
  ) : (
    <button
      type="button"
      onClick={startRename}
      disabled={loading || !project}
      aria-label="Rename draft"
      className="group inline-flex items-center gap-2 text-sm font-medium truncate max-w-full lg:max-w-md text-foreground hover:text-primary transition"
    >
      <span className="truncate">{loading ? '…' : project?.title}</span>
      <Pencil className="h-3.5 w-3.5 opacity-0 group-hover:opacity-60 transition" />
    </button>
  );

  const chatPane = projectId ? (
    <WriterChatPane
      projectId={projectId}
      onToolResult={() => setRefreshTick((t) => t + 1)}
    />
  ) : (
    <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
      Loading project…
    </div>
  );

  return (
    <WriterThreadProvider projectId={projectId}>
      <Helmet>
        <title>{project?.title ?? 'Writer'} · PaperVerse</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      {/* Locked workspace: the page itself never scrolls. On compact this
          is already the case; on desktop/tablet-landscape the same fixed-height
          column makes the PDF pane and the chat each scroll inside themselves,
          independently. */}
      <div className="h-[100dvh] overflow-hidden flex flex-col bg-background">
        {/* The marketing navbar is fixed and costs ~64px of a phone
            viewport while duplicating the Writer header's back link. */}
        {isCompact ? null : <Navbar />}

        {/* Header — full toolbar on desktop, slim on compact surfaces */}
        <div
          className={cn(
            'shrink-0 border-b border-border/40 bg-background/80 backdrop-blur',
            'shadow-[0_10px_30px_-24px_hsl(var(--foreground)/0.35)]',
            isCompact ? '' : 'pt-16 lg:pt-20',
          )}
        >
          <div className="max-w-[1600px] mx-auto px-4 lg:px-5 py-2.5 lg:py-3 flex items-center justify-between gap-3">
            <Link
              to="/write"
              aria-label="All drafts"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden lg:inline">All drafts</span>
            </Link>
            <div className="flex-1 flex justify-start lg:justify-center min-w-0">
              {titleControl}
            </div>
            {isCompact ? (
              <button
                type="button"
                onClick={() => setDraftSheetOpen(true)}
                aria-label={`Draft panels${problemCount > 0 || warningCount > 0 ? `, ${problemCount} problems and ${warningCount} warnings` : ''}`}
                className="relative shrink-0 rounded-md p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition"
              >
                <PanelRight className="h-4 w-4" />
                {problemCount > 0 || warningCount > 0 ? (
                  <span
                    className={cn(
                      'absolute right-1 top-1 h-2 w-2 rounded-full ring-2 ring-background',
                      problemCount > 0 ? 'bg-destructive' : 'bg-warning',
                    )}
                    aria-hidden
                  />
                ) : null}
              </button>
            ) : (
              <div className="flex items-center gap-3">
                {headerActions}
              </div>
            )}
          </div>
        </div>

        {isCompact ? (
          /* Compact shell: fixed-height column — header, status strip and
             composer never scroll; the message list is the only scroller. */
          <main className="flex-1 min-h-0 flex flex-col">
            <WriterMobileStatusBar
              docState={docState}
              versions={versions}
              active={pdfSheetOpen ? 'pdf' : 'chat'}
              onOpenPdf={() => setPdfSheetOpen(true)}
              onShowChat={() => setPdfSheetOpen(false)}
            />
            <div
              className="flex-1 min-h-0 flex flex-col"
              {...swipe}
            >
              {chatPane}
            </div>

            <WriterPdfSheet
              open={pdfSheetOpen}
              onOpenChange={setPdfSheetOpen}
              {...pdfPaneProps}
            />
            {projectId ? (
              <WriterDraftSheet
                open={draftSheetOpen}
                onOpenChange={setDraftSheetOpen}
                projectId={projectId}
                paperMap={paperMap}
                diagnostics={diagnostics}
                paperMapLoading={paperMapLoading}
                refreshTick={refreshTick}
                actions={headerActions}
              />
            ) : null}
          </main>
        ) : (

          /* Desktop / tablet-landscape shell: fixed-height two-pane workspace.
             Each pane scrolls inside itself; the page never scrolls. */
          <main className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-0">
            <section className="border-r border-border/40 bg-muted/20 flex flex-col min-w-0 min-h-0 overflow-hidden">
              <WriterPdfPane {...pdfPaneProps} />
            </section>

            <aside className="flex flex-col min-w-0 min-h-0 overflow-hidden">
              {projectId ? (
                <>
                  <WriterPaperMapPanel
                    paperMap={paperMap}
                    diagnostics={diagnostics}
                    loading={paperMapLoading}
                  />
                  <WriterBibliographyPanel
                    projectId={projectId}
                    refreshTick={refreshTick}
                    citedKeys={paperMap?.sections.flatMap((section) => section.citekeys_used) ?? []}
                  />
                </>
              ) : null}
              {chatPane}
            </aside>
          </main>
        )}
      </div>
    </WriterThreadProvider>

  );
};

export default WriterProject;

